import { Navigate, Outlet } from 'react-router-dom'
import { getErrorMessage } from '@reapit-sales/utilities'
import { FC, ReactNode, useEffect } from 'react'
import { UnauthorizedUserWrapper } from '@crm/components/error-pages/UnauthorizedUserWrapper'
import { useLegacyIframesVisibilityContext } from '@reapit-sales/legacy-crm'
import { usePrivilegeCheck } from './utils'

interface RoutesPrivilegeProps {
  children: ReactNode
}
export const RoutesPrivilege: FC<RoutesPrivilegeProps> = ({ children }) => {
  const { currentPath, defaultPath, privilege } = usePrivilegeCheck()
  const { isLegacyCRMIframeLoaded, setIsLegacyCRMIframeLoaded } = useLegacyIframesVisibilityContext()

  // get default message of 403
  const errorInfo = getErrorMessage(403, '', 'Permission denied')

  /**
   * Resets @isLegacyCRMIframeLoaded from context when the iframe is unmounted or re-rendered.
   * The issue occurs because @Outlet injects the iframe, so we need to check whether it has loaded.
   * To handle this, we call the @setIsLegacyCRMIframeLoaded hook to set @isLegacyCRMIframeLoaded
   * to false when @privilege?.canAccess is not granted.
   */
  useEffect(() => {
    if (!privilege?.canAccess) {
      setIsLegacyCRMIframeLoaded(false)
    }
  }, [defaultPath, currentPath, privilege, isLegacyCRMIframeLoaded])

  /**
   * If no privilege configuration exists for the current route,
   * the user should be redirected to the default path.
   *
   * Example:
   *   - Current route: "/"
   *   - `privilege` is undefined (no config found)
   *   → Redirects to: `defaultPath` (preserves query parameters)
   */
  if (!privilege) {
    return <Navigate to={`${defaultPath}${globalThis.location.search}`} />
  }

  return (
    <UnauthorizedUserWrapper
      errorMessage={errorInfo.message}
      isError={!privilege?.canAccess}
      errorTitle={errorInfo.title}
    >
      {children}
      {isLegacyCRMIframeLoaded && <Outlet />}
    </UnauthorizedUserWrapper>
  )
}
